/**
 * 文件读取工具类
 * 提供文件转换为base64、ArrayBuffer等功能
 */

export interface FileReadResult {
  base64: string
  arrayBuffer: ArrayBuffer
  filename: string
}

export interface FileReadError {
  success: false
  error: string
}

export interface FileReadSuccess {
  success: true
  data: FileReadResult
}

export type FileReadResponse = FileReadSuccess | FileReadError

/**
 * 将文件读取为ArrayBuffer并转换为base64
 * @param file 要读取的文件
 * @returns Promise<FileReadResponse>
 */
export const readFileAsBase64 = (
  file: File | null
): Promise<FileReadResponse> => {
  return new Promise((resolve) => {
    if (!file) {
      resolve({
        success: false,
        error: '文件不存在'
      })
      return
    }

    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer
        if (!arrayBuffer) {
          resolve({
            success: false,
            error: '文件读取结果为空'
          })
          return
        }

        const bytes = new Uint8Array(arrayBuffer)
        const base64 = btoa(String.fromCharCode.apply(null, Array.from(bytes)))

        resolve({
          success: true,
          data: {
            base64,
            arrayBuffer,
            filename: file.name
          }
        })
      } catch (error) {
        resolve({
          success: false,
          error: error instanceof Error ? error.message : '文件处理失败'
        })
      }
    }

    reader.onerror = () => {
      resolve({
        success: false,
        error: '文件读取失败'
      })
    }

    reader.readAsArrayBuffer(file)
  })
}

/**
 * 将文件读取为文本
 * @param file 要读取的文件
 * @param encoding 编码格式，默认为UTF-8
 * @returns Promise<string>
 */
export const readFileAsText = (
  file: File,
  encoding: string = 'UTF-8'
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      resolve(e.target?.result as string)
    }

    reader.onerror = () => {
      reject(new Error('文件读取失败'))
    }

    reader.readAsText(file, encoding)
  })
}

/**
 * 将文件读取为DataURL（用于图片预览等）
 * @param file 要读取的文件
 * @returns Promise<string>
 */
export const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      resolve(e.target?.result as string)
    }

    reader.onerror = () => {
      reject(new Error('文件读取失败'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * 验证文件类型
 * @param file 文件对象
 * @param allowedTypes 允许的文件类型数组
 * @returns boolean
 */
export const validateFileType = (
  file: File,
  allowedTypes: string[]
): boolean => {
  return allowedTypes.some((type) => {
    if (type.includes('*')) {
      // 支持通配符，如 'image/*'
      const baseType = type.split('/')[0]
      return file.type.startsWith(baseType + '/')
    }
    return file.type === type
  })
}

/**
 * 验证文件大小
 * @param file 文件对象
 * @param maxSize 最大文件大小（字节）
 * @returns boolean
 */
export const validateFileSize = (file: File, maxSize: number): boolean => {
  return file.size <= maxSize
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
